import React, { useEffect, useMemo, useRef, useState } from "react";

import { X, Download, ChevronDown, AlertTriangle } from "lucide-react";

import * as XLSX from "xlsx";

import { toast } from "react-hot-toast";

import { attendanceStructureService } from "../../../../../services/api";

import {

  isLeaveTypeApplicableToGender,

  genderMismatchReason,

} from "../../../../../lib/leaveGenderFilter";

import {

  getCompanyServiceCommencementDate,

  getEmployeeDoj,

  getLeaveTypeEffectiveFromDate,

  isEmployeeEligibleForLeaveBalanceYear,

} from "../../../../../lib/leaveBalanceDates";

import { formatDate } from "../../../../../lib/employeeHelpers";

const balancesMatch = (left, right) =>
  Math.abs(Number(left || 0) - Number(right || 0)) < 0.001;

const detectOpeningBalanceConflicts = (
  entries,
  existingBalances,
  groupEmployees,
  leaveTypes
) => {
  const employeeByCode = new Map(
    (groupEmployees || []).map((employee) => [
      String(employee.empCode || "").trim().toLowerCase(),
      employee,
    ])
  );
  const employeeById = new Map(
    (groupEmployees || []).map((employee) => [Number(employee.id), employee])
  );
  const leaveTypeById = new Map(
    (leaveTypes || []).map((leaveType) => [Number(leaveType.id), leaveType])
  );
  const existingByKey = new Map(
    (existingBalances || []).map((row) => [
      `${Number(row.employeeId)}-${Number(row.leaveTypeId)}`,
      row,
    ])
  );

  const conflictMap = new Map();

  (entries || []).forEach((entry) => {
    const employeeCode = String(entry.employeeCode || "").trim();
    const employee =
      employeeByCode.get(employeeCode.toLowerCase()) ||
      employeeById.get(Number(entry.employeeId));
    if (!employee) return;

    const existing = existingByKey.get(
      `${Number(employee.id)}-${Number(entry.leaveTypeId)}`
    );
    if (!existing) return;
    if (balancesMatch(existing.openingBalance, entry.openingBalance)) return;

    const leaveType = leaveTypeById.get(Number(entry.leaveTypeId));
    const mapKey = String(employee.empCode || employeeCode).trim().toLowerCase();

    if (!conflictMap.has(mapKey)) {
      conflictMap.set(mapKey, {
        employeeCode: employee.empCode || employeeCode,
        name: employee.name || existing.employeeName || "",
        gender: employee.gender || existing.gender || "",
        departmentName:
          employee.departmentName || existing.departmentName || "—",
        joiningDate: employee.joiningDate || existing.joiningDate || null,
        leaveTypes: [],
      });
    }

    conflictMap.get(mapKey).leaveTypes.push({
      leaveTypeId: Number(entry.leaveTypeId),
      code: leaveType?.code || existing.leaveTypeCode || String(entry.leaveTypeId),
      name: leaveType?.name || existing.leaveTypeName || "",
      existingBalance: Number(existing.openingBalance),
      uploadedBalance: Number(entry.openingBalance),
    });
  });

  return Array.from(conflictMap.values());
};

const buildConflictEmployeeSkipKeys = (conflicts) =>
  new Set(
    (conflicts || []).map((row) =>
      String(row.employeeCode || "").trim().toLowerCase()
    )
  );

const filterEntriesByEmployeeSkipKeys = (entries, skipEmployeeCodes) =>
  (entries || []).filter(
    (entry) =>
      !skipEmployeeCodes.has(String(entry.employeeCode || "").trim().toLowerCase())
  );

const CODE_HEADER_RE =

  /^(employee\s*code|emp\s*code|employee\s*id|emp\s*id|code)$/i;

const NAME_HEADER_RE = /^(employee\s*name|emp\s*name|name)$/i;

const SL_HEADER_RE = /^(sl\s*no|s\.?\s*no|sr\s*no|#)$/i;

const GENDER_HEADER_RE = /^(gender|sex)$/i;



const normalizeHeader = (value) => String(value || "").trim().toLowerCase();



const buildExcelFilename = (baseName, year) => {

  const now = new Date();

  const month = String(now.getMonth() + 1).padStart(2, "0");

  const stampYear = year || now.getFullYear();

  return `${baseName}_${month}_${stampYear}.xlsx`;

};



const parseOpeningBalanceFile = (file, leaveTypes, groupEmployees, uploadYear) => {

  return new Promise((resolve, reject) => {

    const reader = new FileReader();

    reader.onload = (event) => {

      try {

        const data = new Uint8Array(event.target.result);

        const workbook = XLSX.read(data, { type: "array" });

        const sheet = workbook.Sheets[workbook.SheetNames[0]];

        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });

        if (!rows.length) {

          resolve({ previewRows: [], entries: [], warnings: [] });

          return;

        }



        const headers = (rows[0] || []).map((header) => String(header || "").trim());

        const normalizedHeaders = headers.map(normalizeHeader);



        const codeCol = normalizedHeaders.findIndex((header) =>

          CODE_HEADER_RE.test(header)

        );

        if (codeCol < 0) {

          reject(

            new Error(

              "Sheet must include an Employee Code column (e.g. Employee Code, Emp Code)."

            )

          );

          return;

        }



        const nameCol = normalizedHeaders.findIndex((header) =>

          NAME_HEADER_RE.test(header)

        );



        const leaveTypeByHeader = new Map();

        headers.forEach((header, index) => {

          if (index === codeCol || index === nameCol) return;

          if (SL_HEADER_RE.test(normalizeHeader(header))) return;

          if (GENDER_HEADER_RE.test(normalizeHeader(header))) return;

          const match = leaveTypes.find(

            (leaveType) =>

              normalizeHeader(leaveType.code) === normalizeHeader(header) ||

              normalizeHeader(leaveType.name) === normalizeHeader(header)

          );

          if (match) {

            leaveTypeByHeader.set(index, match);

          }

        });



        if (leaveTypeByHeader.size === 0) {

          const balanceCol = normalizedHeaders.findIndex((header) =>

            /opening\s*balance|balance|leave\s*days|days/.test(header)

          );

          if (balanceCol >= 0 && leaveTypes.length === 1) {

            leaveTypeByHeader.set(balanceCol, leaveTypes[0]);

          } else {

            reject(

              new Error(

                `Add one column per selected leave type using leave type code (${leaveTypes

                  .map((lt) => lt.code)

                  .join(", ")}).`

              )

            );

            return;

          }

        }



        const employeeMap = new Map(

          (groupEmployees || []).map((employee) => [

            String(employee.empCode || "").trim().toLowerCase(),

            employee,

          ])

        );



        const previewRows = [];

        const entries = [];

        const warnings = [];

        const serviceCommencementDate = getCompanyServiceCommencementDate();



        for (let rowIndex = 1; rowIndex < rows.length; rowIndex += 1) {

          const row = rows[rowIndex] || [];

          const employeeCode = String(row[codeCol] ?? "").trim();

          if (!employeeCode) continue;



          const matchedEmployee = employeeMap.get(employeeCode.toLowerCase());

          const balances = {};

          leaveTypeByHeader.forEach((leaveType, columnIndex) => {

            const rawValue = row[columnIndex];

            if (rawValue === "" || rawValue == null) return;

            const normalizedValue = String(rawValue).trim();

            if (/^n\/?a$/i.test(normalizedValue)) return;

            const value = Number(rawValue);

            if (!Number.isFinite(value)) return;



            if (

              matchedEmployee &&

              !isLeaveTypeApplicableToGender(leaveType, matchedEmployee.gender)

            ) {

              warnings.push(

                `${employeeCode}: ${genderMismatchReason(leaveType, matchedEmployee.gender)}`

              );

              return;

            }



            if (

              matchedEmployee &&

              !isEmployeeEligibleForLeaveBalanceYear(uploadYear, {

                serviceCommencementDate,

                employeeDoj: getEmployeeDoj(matchedEmployee),

                effectiveFromDate: leaveType.effectiveFromDate,

              })

            ) {

              warnings.push(

                `${employeeCode}: not eligible for ${leaveType.code} opening balance in ${uploadYear}`

              );

              return;

            }



            balances[leaveType.id] = value;

            entries.push({

              employeeCode,

              leaveTypeId: leaveType.id,

              openingBalance: value,

            });

          });



          if (Object.keys(balances).length === 0) continue;



          const employeeName =

            (nameCol >= 0 ? String(row[nameCol] ?? "").trim() : "") ||

            matchedEmployee?.name ||

            "";



          previewRows.push({

            employeeCode,

            employeeName,

            balances,

            inGroup: Boolean(matchedEmployee),

          });



          if (!matchedEmployee) {

            warnings.push(`${employeeCode} is not assigned to this leave group`);

          }

        }



        resolve({ previewRows, entries, warnings });

      } catch (error) {

        reject(error);

      }

    };

    reader.onerror = () => reject(new Error("Failed to read the selected file."));

    reader.readAsArrayBuffer(file);

  });

};



const LeaveTypeMultiSelect = ({

  darkMode,

  leaveTypes,

  selectedLeaveTypeIds,

  onToggle,

  onSelectAll,

}) => {

  const [open, setOpen] = useState(false);

  const rootRef = useRef(null);



  useEffect(() => {

    const handleClickOutside = (event) => {

      if (rootRef.current && !rootRef.current.contains(event.target)) {

        setOpen(false);

      }

    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);

  }, []);



  const selectedLeaveTypes = leaveTypes.filter((leaveType) =>

    selectedLeaveTypeIds.includes(Number(leaveType.id))

  );



  const displayText =

    selectedLeaveTypes.length === 0

      ? "Select leave types"

      : selectedLeaveTypes.length === leaveTypes.length

        ? `All leave types (${leaveTypes.length})`

        : selectedLeaveTypes.map((lt) => `${lt.name} (${lt.code})`).join(", ");



  return (

    <div className="relative" ref={rootRef}>

      <button

        type="button"

        onClick={() => setOpen((value) => !value)}

        className={`flex w-full items-center justify-between gap-2 rounded border px-3 py-2 text-left text-sm ${

          darkMode

            ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/40 text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"

            : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"

        }`}

      >

        <span className="truncate">{displayText}</span>

        <ChevronDown

          size={16}

          className={`flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}

        />

      </button>

      {open && (

        <div

          className={`absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded border shadow-lg ${

            darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"

          }`}

        >

          {leaveTypes.length > 1 &&
            selectedLeaveTypes.length !== leaveTypes.length && (
            <button
              type="button"
              onClick={() => onSelectAll()}
              className={`block w-full border-b px-3 py-2 text-left text-xs font-medium ${
                darkMode
                  ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
                  : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
              }`}
            >
              Select all
            </button>
          )}

          {leaveTypes.map((leaveType) => {

            const isChecked = selectedLeaveTypeIds.includes(Number(leaveType.id));

            return (

              <label

                key={leaveType.id}

                className={`flex cursor-pointer items-center gap-2 px-3 py-2 text-sm ${

                  darkMode ? "text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]" : "text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"

                }`}

              >

                <input

                  type="checkbox"

                  checked={isChecked}

                  onChange={() => onToggle(leaveType.id)}

                  className="rounded border-[hsl(var(--ds-border))] text-[hsl(var(--ds-primary))] focus:ring-[hsl(var(--ds-ring))]"

                />

                <span

                  className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"

                  style={{ backgroundColor: leaveType.color || "#94a3b8" }}

                />

                <span className="truncate">{leaveType.name}</span>

                <span className={`text-xs ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>

                  ({leaveType.code})

                </span>

              </label>

            );

          })}

        </div>

      )}

    </div>

  );

};



const OpeningBalanceConflictModal = ({
  darkMode,
  conflicts,
  uploading,
  onCancel,
  onSkipConflicting,
  onContinueAll,
}) => (
  <div
    className="fixed inset-0 z-[10060] flex items-center justify-center bg-black bg-opacity-60 p-4"
    onClick={() => !uploading && onCancel()}
  >
    <div
      className={`flex max-h-[90vh] w-full max-w-5xl flex-col rounded-lg shadow-xl ${
        darkMode ? "bg-[hsl(var(--ds-surface))] text-white" : "bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
      }`}
      onClick={(event) => event.stopPropagation()}
    >
      <div
        className={`flex items-start gap-3 border-b p-4 ${
          darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
        }`}
      >
        <AlertTriangle size={24} className="mt-0.5 flex-shrink-0 text-amber-500" />
        <div>
          <h3 className="text-lg font-semibold">Existing opening balance conflict</h3>
          <p className={`mt-2 text-sm ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
            There is an existing opening balance for the given employee(s). If you
            upload this file as-is, it will affect the opening balance and everything
            related to it. Do you still wish to continue?
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div
          className={`overflow-auto rounded border ${
            darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
          }`}
        >
          <table className="w-full text-sm">
            <thead
              className={
                darkMode ? "sticky top-0 bg-[hsl(var(--ds-muted))]" : "sticky top-0 bg-[hsl(var(--ds-muted))]"
              }
            >
              <tr>
                <th className="p-2 text-left">Employee ID</th>
                <th className="p-2 text-left">Name</th>
                <th className="p-2 text-left">Gender</th>
                <th className="p-2 text-left">Department</th>
                <th className="p-2 text-left">DOJ</th>
                <th className="p-2 text-left">Contradicting leave types</th>
              </tr>
            </thead>
            <tbody>
              {conflicts.map((row) => (
                <tr
                  key={row.employeeCode}
                  className={
                    darkMode ? "border-t border-[hsl(var(--ds-border))]" : "border-t border-[hsl(var(--ds-border))]"
                  }
                >
                  <td className="p-2">{row.employeeCode}</td>
                  <td className="p-2">{row.name || "—"}</td>
                  <td className="p-2">{row.gender || "—"}</td>
                  <td className="p-2">{row.departmentName || "—"}</td>
                  <td className="p-2">
                    {row.joiningDate ? formatDate(row.joiningDate) : "—"}
                  </td>
                  <td className="p-2">
                    {(row.leaveTypes || [])
                      .map(
                        (leaveType) =>
                          `${leaveType.code} (existing ${leaveType.existingBalance}, upload ${leaveType.uploadedBalance})`
                      )
                      .join("; ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div
        className={`flex flex-wrap justify-end gap-2 border-t p-4 ${
          darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
        }`}
      >
        <button
          type="button"
          onClick={onCancel}
          disabled={uploading}
          className={`rounded px-4 py-2 text-sm font-medium ${
            darkMode
              ? "bg-[hsl(var(--ds-muted))] hover:bg-[hsl(var(--ds-muted))]"
              : "bg-[hsl(var(--ds-muted))] hover:bg-[hsl(var(--ds-muted))]"
          } disabled:cursor-not-allowed disabled:opacity-50`}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSkipConflicting}
          disabled={uploading}
          className={`rounded px-4 py-2 text-sm font-medium ${
            darkMode
              ? "bg-amber-700 text-white hover:bg-amber-600"
              : "bg-amber-100 text-amber-900 hover:bg-amber-200"
          } disabled:cursor-not-allowed disabled:opacity-50`}
        >
          {uploading ? "Uploading..." : "No, skip these employees"}
        </button>
        <button
          type="button"
          onClick={onContinueAll}
          disabled={uploading}
          className="rounded px-4 py-2 text-sm font-medium inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {uploading ? "Uploading..." : "Yes, continue"}
        </button>
      </div>
    </div>
  </div>
);



const OpeningBalanceUploadModal = ({ darkMode, leaveGroup, onClose, onSuccess }) => {

  const uploadYear = new Date().getFullYear();

  const [groupEmployees, setGroupEmployees] = useState([]);

  const [loadingEmployees, setLoadingEmployees] = useState(true);

  const [previewRows, setPreviewRows] = useState([]);

  const [entries, setEntries] = useState([]);

  const [warnings, setWarnings] = useState([]);

  const [uploading, setUploading] = useState(false);

  const [checkingConflicts, setCheckingConflicts] = useState(false);

  const [showConflictModal, setShowConflictModal] = useState(false);

  const [conflictRows, setConflictRows] = useState([]);

  const [cleanupRemoved, setCleanupRemoved] = useState(0);

  const [selectedLeaveTypeIds, setSelectedLeaveTypeIds] = useState([]);

  const leaveTypes = useMemo(

    () => leaveGroup?.leaveTypes || [],

    [leaveGroup?.leaveTypes]

  );



  useEffect(() => {

    setSelectedLeaveTypeIds(leaveTypes.map((leaveType) => leaveType.id));

  }, [leaveTypes]);



  const selectedLeaveTypes = useMemo(() => {

    const selected = new Set(selectedLeaveTypeIds.map(Number));

    return leaveTypes

      .filter((leaveType) => selected.has(Number(leaveType.id)))

      .map((leaveType) => ({

        ...leaveType,

        effectiveFromDate: getLeaveTypeEffectiveFromDate(leaveGroup, leaveType.id),

      }));

  }, [leaveTypes, selectedLeaveTypeIds, leaveGroup]);



  const serviceCommencementDate = useMemo(
    () => getCompanyServiceCommencementDate(),
    []
  );

  useEffect(() => {
    setPreviewRows([]);
    setEntries([]);
    setWarnings([]);
    setShowConflictModal(false);
    setConflictRows([]);
  }, [selectedLeaveTypeIds]);



  useEffect(() => {

    let active = true;

    const loadEmployees = async () => {

      if (!leaveGroup?.id) return;

      setLoadingEmployees(true);

      try {

        const response = await attendanceStructureService.getLeaveGroupEmployees(

          leaveGroup.id

        );

        if (active) {

          setGroupEmployees(response?.data || []);

        }

        if (active && leaveGroup?.id) {

          try {

            const cleanup = await attendanceStructureService.cleanupLeaveOpeningBalanceByGender(

              leaveGroup.id,

              uploadYear

            );

            if (active && cleanup?.removed > 0) {

              setCleanupRemoved(cleanup.removed);

            }

          } catch {

            // Non-blocking: upload still enforces gender rules

          }

        }

      } catch (error) {

        if (active) {

          toast.error(

            error?.response?.data?.message ||

              error?.message ||

              "Failed to load employees for this leave group."

          );

          setGroupEmployees([]);

        }

      } finally {

        if (active) {

          setLoadingEmployees(false);

        }

      }

    };

    loadEmployees();

    return () => {

      active = false;

    };

  }, [leaveGroup?.id, uploadYear]);



  const templateEmployees = useMemo(() => {

    return groupEmployees.filter((employee) =>

      selectedLeaveTypes.some((leaveType) =>

        isEmployeeEligibleForLeaveBalanceYear(uploadYear, {

          serviceCommencementDate,

          employeeDoj: getEmployeeDoj(employee),

          effectiveFromDate: leaveType.effectiveFromDate,

        })

      )

    );

  }, [groupEmployees, selectedLeaveTypes, uploadYear, serviceCommencementDate]);



  const toggleLeaveType = (leaveTypeId) => {

    setSelectedLeaveTypeIds((prev) => {

      const id = Number(leaveTypeId);

      if (prev.includes(id)) {

        if (prev.length === 1) return prev;

        return prev.filter((value) => Number(value) !== id);

      }

      return [...prev, id];

    });

  };



  const handleSelectAllLeaveTypes = () => {
    setSelectedLeaveTypeIds(leaveTypes.map((lt) => lt.id));
  };



  const handleFileChange = async (event) => {

    const file = event.target.files?.[0];

    event.target.value = "";

    if (!file) return;



    if (selectedLeaveTypes.length === 0) {

      toast.error("Select at least one leave type.");

      return;

    }



    try {

      const parsed = await parseOpeningBalanceFile(

        file,

        selectedLeaveTypes,

        groupEmployees,

        uploadYear

      );

      setPreviewRows(parsed.previewRows);

      setEntries(parsed.entries);

      setWarnings(parsed.warnings);

      setShowConflictModal(false);

      setConflictRows([]);

      if (!parsed.previewRows.length) {

        toast.error("No opening balance values found in the uploaded file.");

      }

    } catch (error) {

      toast.error(error.message || "Failed to parse the uploaded file.");

      setPreviewRows([]);

      setEntries([]);

      setWarnings([]);

    }

  };



  const handleDownloadTemplate = () => {

    if (selectedLeaveTypes.length === 0) {

      toast.error("Select at least one leave type.");

      return;

    }



    const headers = [

      "Sl No",

      "Employee Code",

      "Employee Name",

      "Gender",

      ...selectedLeaveTypes.map((leaveType) => leaveType.code),

    ];

    const rows = templateEmployees.map((employee, index) => [

      index + 1,

      employee.empCode || "",

      employee.name || "",

      employee.gender || "",

      ...selectedLeaveTypes.map((leaveType) => {

        if (!isLeaveTypeApplicableToGender(leaveType, employee.gender)) {

          return "N/A";

        }

        if (

          !isEmployeeEligibleForLeaveBalanceYear(uploadYear, {

            serviceCommencementDate,

            employeeDoj: getEmployeeDoj(employee),

            effectiveFromDate: leaveType.effectiveFromDate,

          })

        ) {

          return "N/A";

        }

        return "";

      }),

    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    worksheet["!cols"] = [

      { wch: 8 },

      { wch: 18 },

      { wch: 28 },

      { wch: 10 },

      ...selectedLeaveTypes.map(() => ({ wch: 14 })),

    ];

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Opening Balance");

    const safeName = (leaveGroup?.name || "LeaveGroup")

      .replace(/[^\w\s-]/g, "")

      .replace(/\s+/g, "_");

    XLSX.writeFile(

      workbook,

      buildExcelFilename(`Opening_Balance_${safeName}`, uploadYear)

    );

    toast.success("Template downloaded. Fill leave type columns and upload.");

  };



  const performUpload = async (entriesToUpload) => {
    if (!entriesToUpload.length) {
      toast.error("No records left to upload after skipping conflicting employees.");
      return;
    }

    setUploading(true);
    try {
      const response = await attendanceStructureService.uploadLeaveOpeningBalance(
        leaveGroup.id,
        entriesToUpload,
        uploadYear
      );

      if (response?.success === false) {
        toast.error(response?.message || "Upload failed.");
        return;
      }

      const skipped = response?.skipped || [];
      const removedByCleanup = response?.cleanupRemoved || 0;

      if (skipped.length > 0) {
        toast.error(
          `${response?.upserted || 0} updated, ${skipped.length} skipped. Check employee codes, gender, leave group assignment, and eligibility dates.`,
          { duration: 5000 }
        );
      } else {
        toast.success(
          response?.message ||
            `Opening balance updated for ${response?.upserted || entriesToUpload.length} record(s).`
        );
      }

      if (removedByCleanup > 0) {
        setCleanupRemoved(removedByCleanup);
      }

      setShowConflictModal(false);
      setConflictRows([]);
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to upload opening balance."
      );
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async () => {
    if (!entries.length) {
      toast.error("Upload a file with at least one opening balance value.");
      return;
    }

    if (selectedLeaveTypes.length === 0) {
      toast.error("Select at least one leave type.");
      return;
    }

    setCheckingConflicts(true);
    try {
      const response = await attendanceStructureService.getLeaveOpeningBalancesForGroup(
        leaveGroup.id,
        uploadYear
      );
      const existingBalances = response?.data || [];
      const conflicts = detectOpeningBalanceConflicts(
        entries,
        existingBalances,
        groupEmployees,
        selectedLeaveTypes
      );

      if (conflicts.length > 0) {
        setConflictRows(conflicts);
        setShowConflictModal(true);
        return;
      }

      await performUpload(entries);
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to check existing opening balances."
      );
    } finally {
      setCheckingConflicts(false);
    }
  };

  const handleConflictContinueAll = async () => {
    await performUpload(entries);
  };

  const handleConflictSkipConflicting = async () => {
    const skipEmployeeCodes = buildConflictEmployeeSkipKeys(conflictRows);
    const filteredEntries = filterEntriesByEmployeeSkipKeys(entries, skipEmployeeCodes);
    await performUpload(filteredEntries);
  };

  const handleConflictCancel = () => {
    if (uploading) return;
    setShowConflictModal(false);
    setConflictRows([]);
  };



  return (

    <>

      <div

        className="fixed inset-0 z-[10050] flex items-center justify-center bg-black bg-opacity-50"

        onClick={() => !uploading && !checkingConflicts && onClose()}

      >

      <div

        className={`flex max-h-[90vh] w-full max-w-4xl flex-col rounded-lg shadow-xl ${

          darkMode ? "bg-[hsl(var(--ds-surface))] text-white" : "bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"

        }`}

        onClick={(event) => event.stopPropagation()}

      >

        <div className="flex flex-shrink-0 items-center justify-between border-b border-[hsl(var(--ds-border))] p-4">

          <div>

            <h3 className="text-lg font-semibold">

              Upload Opening Balance – {leaveGroup?.name}

            </h3>

            <p

              className={`mt-1 text-xs ${

                darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"

              }`}

            >

              Opening balance applies from the latest of service commencement date, leave

              type effective date, and employee DOJ. If DOJ is after the effective date,

              balance starts from DOJ.

            </p>

          </div>

          <button

            type="button"

            onClick={() => !uploading && !checkingConflicts && onClose()}

            className={`rounded p-1 ${

              darkMode ? "hover:bg-[hsl(var(--ds-muted))]" : "hover:bg-[hsl(var(--ds-muted))]"

            }`}

          >

            <X size={20} />

          </button>

        </div>



        <div className="flex-1 space-y-4 overflow-y-auto p-4">

          <div

            className={`rounded-md border p-3 text-sm ${

              darkMode

                ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/40 text-[hsl(var(--ds-fg))]"

                : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]"

            }`}

          >

            {loadingEmployees

              ? "Loading active employees assigned to this leave group..."

              : `${groupEmployees.length} active employee(s) in this leave group (excludes relieved, terminated, resigned, and locked rejoin records).${

                  templateEmployees.length !== groupEmployees.length

                    ? ` ${templateEmployees.length} eligible with selected leave types.`

                    : ""

                }${

                  cleanupRemoved > 0

                    ? ` Removed ${cleanupRemoved} gender-ineligible opening balance record(s).`

                    : ""

                }`}

          </div>



          <div>
            <label
              className={`mb-1 block text-sm font-medium ${
                darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"
              }`}
            >
              Leave types *
            </label>
              {leaveTypes.length === 0 ? (
                <p className={`text-xs ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                  No leave types in this group.
                </p>
              ) : (
                <LeaveTypeMultiSelect
                  darkMode={darkMode}
                  leaveTypes={leaveTypes}
                  selectedLeaveTypeIds={selectedLeaveTypeIds}
                  onToggle={toggleLeaveType}
                  onSelectAll={handleSelectAllLeaveTypes}
                />
              )}
              <p className={`mt-1 text-xs ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                {selectedLeaveTypes.length} of {leaveTypes.length} selected. Template
                columns and upload use only selected types.
              </p>
          </div>



          <div>

            <label

              className={`mb-1 block text-sm font-medium ${

                darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"

              }`}

            >

              Excel file

            </label>

            <div className="flex flex-wrap items-center gap-2">

              <button

                type="button"

                onClick={handleDownloadTemplate}

                disabled={loadingEmployees || selectedLeaveTypes.length === 0}

                className={`inline-flex items-center gap-1.5 rounded px-3 py-2 text-sm font-medium ${

                  darkMode

                    ? "bg-[hsl(var(--ds-muted))] text-white hover:bg-[hsl(var(--ds-muted))]"

                    : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"

                } disabled:cursor-not-allowed disabled:opacity-50`}

              >

                <Download size={16} />

                Download template

              </button>

              <span className={`text-xs ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>

                or

              </span>

              <input

                type="file"

                accept=".xlsx,.xls"

                onChange={handleFileChange}

                disabled={loadingEmployees || uploading || selectedLeaveTypes.length === 0}

                className={`text-sm ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"}`}

              />

            </div>

          </div>



          {warnings.length > 0 && (

            <div

              className={`rounded-md border p-3 text-xs ${

                darkMode

                  ? "border-amber-700 bg-amber-900/20 text-amber-200"

                  : "border-amber-200 bg-amber-50 text-amber-800"

              }`}

            >

              {warnings.slice(0, 5).map((warning) => (

                <div key={warning}>{warning}</div>

              ))}

              {warnings.length > 5 ? (

                <div>...and {warnings.length - 5} more warning(s)</div>

              ) : null}

            </div>

          )}



          {previewRows.length > 0 && (

            <div>

              <h4

                className={`mb-2 text-sm font-medium ${

                  darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"

                }`}

              >

                Preview

              </h4>

              <div

                className={`max-h-72 overflow-auto rounded border ${

                  darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"

                }`}

              >

                <table className="w-full text-sm">

                  <thead className={darkMode ? "sticky top-0 bg-[hsl(var(--ds-muted))]" : "sticky top-0 bg-[hsl(var(--ds-muted))]"}>

                    <tr>

                      <th className="p-2 text-left">#</th>

                      <th className="p-2 text-left">Employee Code</th>

                      <th className="p-2 text-left">Employee Name</th>

                      {selectedLeaveTypes.map((leaveType) => (

                        <th key={leaveType.id} className="p-2 text-right">

                          {leaveType.code}

                        </th>

                      ))}

                      <th className="p-2 text-left">Status</th>

                    </tr>

                  </thead>

                  <tbody>

                    {previewRows.map((row, index) => (

                      <tr

                        key={`${row.employeeCode}-${index}`}

                        className={

                          darkMode ? "border-t border-[hsl(var(--ds-border))]" : "border-t border-[hsl(var(--ds-border))]"

                        }

                      >

                        <td className="p-2">{index + 1}</td>

                        <td className="p-2">{row.employeeCode}</td>

                        <td className="p-2">{row.employeeName || "—"}</td>

                        {selectedLeaveTypes.map((leaveType) => (

                          <td key={leaveType.id} className="p-2 text-right">

                            {row.balances?.[leaveType.id] ?? "—"}

                          </td>

                        ))}

                        <td className="p-2">

                          {row.inGroup ? (

                            <span className="text-emerald-600">Ready</span>

                          ) : (

                            <span className="text-amber-600">Not in group</span>

                          )}

                        </td>

                      </tr>

                    ))}

                  </tbody>

                </table>

              </div>

              <p

                className={`mt-1 text-xs ${

                  darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"

                }`}

              >

                {entries.length} balance record(s) will be uploaded.

              </p>

            </div>

          )}

        </div>



        <div

          className={`flex justify-end gap-2 border-t p-4 ${

            darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"

          }`}

        >

          <button

            type="button"

            onClick={() => !uploading && !checkingConflicts && onClose()}

            className={`rounded px-4 py-2 text-sm font-medium ${

              darkMode

                ? "bg-[hsl(var(--ds-muted))] hover:bg-[hsl(var(--ds-muted))]"

                : "bg-[hsl(var(--ds-muted))] hover:bg-[hsl(var(--ds-muted))]"

            }`}

          >

            Cancel

          </button>

          <button

            type="button"

            onClick={handleUpload}

            disabled={

              entries.length === 0 ||

              uploading ||

              checkingConflicts ||

              loadingEmployees ||

              selectedLeaveTypes.length === 0

            }

            className="rounded px-4 py-2 text-sm font-medium inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"

          >

            {uploading

              ? "Uploading..."

              : checkingConflicts

                ? "Checking..."

                : "Upload"}

          </button>

        </div>

      </div>

      </div>

      {showConflictModal && conflictRows.length > 0 ? (
        <OpeningBalanceConflictModal
          darkMode={darkMode}
          conflicts={conflictRows}
          uploading={uploading}
          onCancel={handleConflictCancel}
          onSkipConflicting={handleConflictSkipConflicting}
          onContinueAll={handleConflictContinueAll}
        />
      ) : null}

    </>

  );

};



export default OpeningBalanceUploadModal;

